/**
 * PriceFilter — per-column price-masked filter input.
 *
 * Round 3 of the per-filter design-system migration. Renders the
 * trigger through `@shared/ui/Input` primitive (Tier 0 form-field
 * spec). `react-currency-format` continues to own the `$ ` prefix,
 * comma thousand-separator and 2-decimal mask.
 *
 * Behaviour preserved 1:1 vs. legacy:
 *   - Local `useState` mirrored from `filterValue` so the masked
 *     `<input>` can stay controlled by react-currency-format.
 *   - `onValueChange` commits continuously through
 *     `onChange(dataKey, value, true)` (instant filter).
 *   - Enter triggers the same handler.
 *
 * `highlighted` is forwarded as a `hasFilterValue` passthrough prop
 * from the parent — react-currency-format spreads anything not in
 * its own propTypes onto customInput, and reading the parent's raw
 * filterValue is more reliable than the masked value, which lags
 * one render behind on mount when a table opens with a pre-set
 * filter.
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import CurrencyFormat from 'react-currency-format';
import Input from '@shared/ui/Input';

const PriceFilter = props => {
  const [value, setValue] = useState('');
  const { filterValue = '', dataKey, onChange } = props;

  useEffect(() => {
    if (filterValue === '') {
      setValue('');
    } else if (value === '' && filterValue !== null) {
      setValue(filterValue);
    }
  }, [filterValue]);

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      const next = e.target.value.replace('$ ', '');
      if (onChange) {
        onChange(dataKey, next, true);
      }
    }
  };

  return (
    <CurrencyFormat
      prefix={'$ '}
      value={value}
      customInput={_CustomInput}
      // Custom passthrough — see CurrencyTextFilter for the full
      // rationale (masked value lags on mount, parent filterValue
      // is reliable on every render).
      hasFilterValue={!!filterValue}
      allowNegative={false}
      fixedDecimalScale={true}
      decimalScale={2}
      onKeyDown={handleKeyDown}
      thousandSeparator={','}
      onValueChange={values => {
        const { value: next } = values;
        setValue(next);
        onChange(dataKey, next, true);
      }}
    />
  );
};

PriceFilter.propTypes = {
  filterValue: PropTypes.string,
  dataKey: PropTypes.string.isRequired,
  columnData: PropTypes.shape({
    isFilterable: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func,
};

export default PriceFilter;

const _CustomInput = ({ inputRef, hasFilterValue, ...props }) => {
  const { tables } = useSelector(state => state.i18n);
  return (
    <Input
      {...props}
      ref={inputRef}
      size="sm"
      fullWidth
      placeholder={!props.value ? tables.filter : ''}
      autoComplete="off"
      highlighted={!!hasFilterValue}
    />
  );
};
